// covered-by: frontends/carrie/src/ask-v2/review/__tests__/useAskV2ReviewWorkspace.test.ts
import { useNavigate } from "@tanstack/react-router";
import { useCallback, useMemo, useState } from "react";

import {
  ASK_V2_CHAT_PATH,
  ASK_V2_PROJECT_PATH,
  buildAskV2ChatReviewPath,
  buildAskV2ProjectReviewPath,
} from "../paths";

export type AskV2ReviewLeftPane = "comments" | "feed";

export type AskV2ReviewWorkspaceScope =
  | { kind: "project"; projectId: string }
  | { kind: "chat"; chatId: string };

export function useAskV2ReviewWorkspace(options: {
  scope: AskV2ReviewWorkspaceScope;
  reviewId?: string | null;
  commentId?: string | null;
}) {
  const navigate = useNavigate();
  const { scope, reviewId = null, commentId = null } = options;
  const [leftPane, setLeftPaneState] =
    useState<AskV2ReviewLeftPane>("comments");

  const isReviewOpen = reviewId != null && reviewId.length > 0;

  const openReview = useCallback(
    (nextReviewId: string) => {
      setLeftPaneState("comments");
      const location =
        scope.kind === "project"
          ? buildAskV2ProjectReviewPath(scope.projectId, nextReviewId)
          : buildAskV2ChatReviewPath(scope.chatId, nextReviewId);
      navigate(location as never);
    },
    [navigate, scope],
  );

  const closeReview = useCallback(() => {
    setLeftPaneState("comments");
    navigate({
      to: scope.kind === "project" ? ASK_V2_PROJECT_PATH : ASK_V2_CHAT_PATH,
      params:
        scope.kind === "project"
          ? { projectId: scope.projectId }
          : { chatId: scope.chatId },
    });
  }, [navigate, scope]);

  const selectComment = useCallback(
    (nextCommentId: string) => {
      if (!reviewId) return;
      const location =
        scope.kind === "project"
          ? buildAskV2ProjectReviewPath(
              scope.projectId,
              reviewId,
              nextCommentId,
            )
          : buildAskV2ChatReviewPath(scope.chatId, reviewId, nextCommentId);
      navigate(location as never);
    },
    [navigate, reviewId, scope],
  );

  const clearComment = useCallback(() => {
    if (!reviewId) return;
    openReview(reviewId);
  }, [openReview, reviewId]);

  const switchReview = useCallback(
    (nextReviewId: string) => {
      openReview(nextReviewId);
    },
    [openReview],
  );

  const setLeftPane = useCallback((pane: AskV2ReviewLeftPane) => {
    setLeftPaneState(pane);
  }, []);

  return useMemo(
    () => ({
      isReviewOpen,
      reviewId,
      commentId,
      leftPane,
      openReview,
      closeReview,
      selectComment,
      clearComment,
      switchReview,
      setLeftPane,
    }),
    [
      clearComment,
      closeReview,
      commentId,
      isReviewOpen,
      leftPane,
      openReview,
      reviewId,
      selectComment,
      setLeftPane,
      switchReview,
    ],
  );
}
